import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, ImagePlus, Sparkles, Heart, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

function imgSrc(item) {
  const url = item.image_url || "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

function GalleryCard({ item, user, onRemove, onChange }) {
  const [liked, setLiked] = useState(item.liked);
  const [likeCount, setLikeCount] = useState(item.like_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(item.comment_count || 0);
  const [body, setBody] = useState("");

  const toggleLike = async () => {
    if (!user) return toast.error("Sign in to react");
    try {
      const { data } = await api.post(`/gallery/${item._id}/like`);
      setLiked(data.liked);
      setLikeCount(data.like_count);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const loadComments = async () => {
    const { data } = await api.get(`/gallery/${item._id}/comments`);
    setComments(data);
    setCommentCount(data.length);
  };

  const openComments = () => {
    const next = !showComments;
    setShowComments(next);
    if (next) loadComments();
  };

  const postComment = async () => {
    if (!body.trim()) return;
    try {
      await api.post(`/gallery/${item._id}/comments`, { body });
      setBody("");
      loadComments();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const initials = (n) => (n ? n.slice(0, 2).toUpperCase() : "?");

  return (
    <div data-testid={`gallery-item-${item._id}`} className="break-inside-avoid bg-white border border-[#6A0DAD] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all group relative">
      {item.featured && (
        <span className="absolute top-3 left-3 z-10 px-3 py-1 rounded-full bg-[#D4AF37] text-white text-xs font-heading font-semibold flex items-center gap-1">
          <Sparkles size={12} /> Featured
        </span>
      )}
      {user && (item.uploader_id === user._id || user.role === "admin") && (
        <button data-testid={`gallery-delete-${item._id}`} onClick={() => onRemove(item._id)}
          className="absolute top-3 right-3 z-10 grid place-items-center w-8 h-8 rounded-full bg-white/90 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
          <Trash2 size={15} />
        </button>
      )}
      <img src={imgSrc(item)} alt={item.caption || "artwork"} loading="lazy" className="w-full object-cover" />
      <div className="p-4">
        {item.caption && <p className="text-[#1A202C] leading-relaxed">{item.caption}</p>}
        <p className="text-xs text-[#4A5568] font-heading mt-1.5">— {item.uploader_name}</p>

        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#6A0DAD]">
          <button data-testid={`gallery-like-${item._id}`} onClick={toggleLike}
            className={`flex items-center gap-1.5 font-heading text-sm transition-colors ${liked ? "text-[#D4AF37]" : "text-[#4A5568] hover:text-[#D4AF37]"}`}>
            <Heart size={17} fill={liked ? "#D4AF37" : "none"} /> {likeCount}
          </button>
          <button data-testid={`gallery-comment-toggle-${item._id}`} onClick={openComments}
            className="flex items-center gap-1.5 font-heading text-sm text-[#4A5568] hover:text-[#6B8EAC] transition-colors">
            <MessageCircle size={17} /> {commentCount}
          </button>
        </div>

        {showComments && (
          <div className="mt-3 space-y-2.5">
            {comments.map((c) => (
              <div key={c._id} data-testid={`gallery-comment-${c._id}`} className="text-sm">
                <span className="font-heading font-semibold text-[#1A202C]">{c.author_name}</span>{" "}
                <span className="text-[#4A5568]">{c.body}</span>
              </div>
            ))}
            {user ? (
              <div className="flex gap-2 items-center pt-1">
                <input data-testid={`gallery-comment-input-${item._id}`} value={body} onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && postComment()}
                  placeholder="Add a kind word…" className="flex-1 border border-[#6A0DAD] rounded-full px-3 py-1.5 text-sm outline-none focus:border-[#6B8EAC]" />
                <button data-testid={`gallery-comment-submit-${item._id}`} onClick={postComment}
                  className="grid place-items-center bg-[#6B8EAC] text-white rounded-full w-8 h-8 shrink-0 hover:bg-[#5A7B96] transition-colors">
                  <Send size={14} />
                </button>
              </div>
            ) : (
              <Link to="/auth" className="text-sm text-[#6B8EAC] font-heading">Sign in to comment</Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Gallery() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [kind, setKind] = useState("art");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = useCallback(() => api.get("/gallery").then((r) => setItems(r.data.filter((i) => i.image_url))).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const upload = async () => {
    if (!file) return toast.error("Please choose an image");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      fd.append("caption", caption);
      await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Added to the gallery 🎨");
      setOpen(false);
      setFile(null); setPreview(null); setCaption("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const shown = tab === "all" ? items : items.filter((i) => i.kind === tab);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl md:text-5xl text-[#1A202C] flex items-center gap-3">
            <Sparkles className="text-[#D4AF37]" /> Miracle Gallery
          </h1>
          <p className="text-[#4A5568] mt-2">Art, murals and joyful milestones shared by our families.</p>
        </div>
        {user ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="gallery-add-btn" className="inline-flex items-center gap-2 bg-[#D4AF37] text-white hover:bg-[#B8972E] rounded-full px-6 py-3 font-heading font-semibold transition-colors shadow-sm">
                <Plus size={18} /> Add artwork
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle className="font-heading">Share to the gallery</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="flex gap-2">
                  {[["art", "Artwork"], ["milestone", "Milestone"]].map(([k, label]) => (
                    <button key={k} data-testid={`gallery-kind-${k}`} onClick={() => setKind(k)}
                      className={`flex-1 py-2 rounded-xl font-heading text-sm transition-colors ${kind === k ? "bg-[#6B8EAC] text-white" : "bg-white border border-[#6A0DAD] text-[#4A5568]"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <input ref={fileRef} data-testid="gallery-file-input" type="file" accept="image/*" onChange={pick} className="hidden" />
                <button data-testid="gallery-choose-file" onClick={() => fileRef.current?.click()}
                  className="w-full border-2 border-dashed border-[#6A0DAD] rounded-xl py-8 flex flex-col items-center gap-2 text-[#4A5568] hover:border-[#6B8EAC] transition-colors">
                  {preview ? (
                    <img src={preview} alt="preview" className="max-h-40 rounded-lg object-contain" />
                  ) : (
                    <><ImagePlus size={28} /><span className="font-heading text-sm">Choose an image</span></>
                  )}
                </button>
                <input data-testid="gallery-caption-input" placeholder="Add a caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC]" />
              </div>
              <DialogFooter>
                <button data-testid="gallery-upload-btn" onClick={upload} disabled={uploading} className="bg-[#D4AF37] text-white hover:bg-[#B8972E] rounded-full px-6 py-2.5 font-heading font-semibold transition-colors disabled:opacity-60">
                  {uploading ? "Uploading…" : "Share"}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : (
          <Link to="/auth" className="bg-[#D4AF37] text-white rounded-full px-6 py-3 font-heading font-semibold">Sign in to share</Link>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        {[["all", "All"], ["art", "Artwork"], ["milestone", "Milestones"]].map(([k, label]) => (
          <button key={k} data-testid={`gallery-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-1.5 rounded-full text-sm font-heading transition-colors ${tab === k ? "bg-[#6B8EAC] text-white" : "bg-white border border-[#6A0DAD] text-[#4A5568]"}`}>
            {label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="bg-white border border-[#6A0DAD] rounded-2xl p-12 text-center text-[#4A5568]">
          No artwork yet. Be the first to share something joyful.
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:mb-4">
          {shown.map((i) => (
            <GalleryCard key={i._id} item={i} user={user} onRemove={remove} />
          ))}
        </div>
      )}
    </div>
  );
}
